import React, { useRef, useState } from "react";
import api, { downloadBlob, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileCheck2, Download, Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import DeadlineList from "@/components/contract/DeadlineList";

export default function SignedPanel({ offerId, contract, setContract, isAdmin }) {
  const input = useRef(null);
  const [busy, setBusy] = useState("");
  const sf = contract.signed_file;
  const analysis = contract.analysis;

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run("upload", async () => {
      const { data } = await api.post(`/offers/${offerId}/contract/signed`, fd);
      setContract(data);
    }, "Contratto firmato caricato");
  };

  const analyze = () => run("ai", async () => {
    const { data } = await api.post(`/offers/${offerId}/contract/analyze`, {}, { timeout: 180000 });
    setContract(data);
  }, "Analisi completata");

  const send = () => run("prod", async () => {
    const { data } = await api.post(`/offers/${offerId}/contract/send-production`);
    setContract(data);
  }, "Email inviata alla produzione");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <input ref={input} type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" className="hidden" onChange={upload} data-testid="signed-file-input" />
        <Button variant={sf ? "outline" : "default"} onClick={() => input.current?.click()} disabled={busy === "upload"} data-testid="upload-signed-button" className="gap-2 font-bold">
          {busy === "upload" ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {sf ? "Sostituisci firmato" : "Carica contratto firmato"}
        </Button>
        {sf && (
          <>
            <span className="flex items-center gap-2 text-sm text-navy font-semibold" data-testid="signed-file-name">
              <FileCheck2 size={16} className="text-primary" /> {sf.filename} · {(sf.uploaded_at || "").slice(0, 10)}
            </span>
            <Button variant="ghost" size="sm" onClick={() => downloadBlob(`/offers/${offerId}/contract/signed`, sf.filename)} data-testid="download-signed-button" className="gap-1">
              <Download size={14} /> Scarica
            </Button>
          </>
        )}
      </div>

      {sf && (
        <div className="rounded-xl border border-border p-5 bg-secondary/40">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h4 className="font-extrabold text-navy flex items-center gap-2"><Sparkles size={16} className="text-primary" /> Analisi AI scadenze</h4>
            <Button size="sm" variant="outline" onClick={analyze} disabled={busy === "ai"} data-testid="analyze-contract-button" className="gap-2 font-bold">
              {busy === "ai" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {analysis ? "Rianalizza" : "Analizza con AI"}
            </Button>
          </div>
          {busy === "ai" && <p className="text-sm text-muted-foreground">Sto leggendo il contratto, può richiedere fino a un minuto…</p>}
          {analysis && busy !== "ai" && (
            <>
              <p className="text-sm text-navy mb-4" data-testid="analysis-summary">{analysis.summary}</p>
              {analysis.source === "generated" && <p className="text-xs text-amber-700 mb-3">Il file firmato non contiene testo leggibile (scansione): analisi basata sul contratto generato.</p>}
              <DeadlineList items={analysis.deadlines} offerId={offerId} />
            </>
          )}
          {!analysis && busy !== "ai" && <p className="text-sm text-muted-foreground">L'AI individua rinnovi, termini di disdetta, fine sconti e pagamenti e li aggiunge alla sezione Scadenze.</p>}
        </div>
      )}

      {sf && isAdmin && (
        <div className="rounded-xl border border-dashed border-navy/30 p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-extrabold text-navy">Attivazione in produzione</h4>
            <p className="text-sm text-muted-foreground" data-testid="production-status">
              {contract.production?.sent_at ? `Inviato il ${contract.production.sent_at.slice(0, 10)} da ${contract.production.sent_by}${contract.production.sent_to ? ` a ${contract.production.sent_to}` : ""}` : "Invia via email alla produzione i dati del cliente, moduli, marketplace e integrazioni (senza prezzi)."}
            </p>
          </div>
          <Button onClick={send} disabled={busy === "prod"} data-testid="send-production-button" className="gap-2 font-bold">
            {busy === "prod" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Invia alla produzione
          </Button>
        </div>
      )}
    </div>
  );
}
